/*
 * A thin wrapper over Google's Picker, which is the sanctioned way to let a
 * user hand us specific Drive files.
 *
 * Why this exists at all: our own browse UI needed
 * `https://www.googleapis.com/auth/drive.readonly` to list a user's Drive, and
 * that is a Google *restricted* scope. An app requesting it must pass app
 * verification plus an annual third-party CASA security assessment, and until
 * it does, everyone outside the test-user list sees "Google hasn't verified
 * this app". That is exactly the "source not trusted" the client hit.
 *
 * The Picker runs inside Google's own iframe, so the file listing happens under
 * the user's session rather than ours. We only need `drive.file`, which is
 * non-sensitive and grants access to precisely the files the user picks —
 * no verification, no CASA, no 100-user cap.
 *
 * `react-google-drive-picker` is deliberately not used: it hardcodes
 * `drive.readonly` in its default scopes and appends custom scopes rather than
 * replacing them, so it cannot request `drive.file` alone.
 */

const PICKER_SCRIPT_SRC = "https://apis.google.com/js/api.js";

export const DRIVE_FILE_SCOPE = "https://www.googleapis.com/auth/drive.file";

export type PickedFile = {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  webViewLink?: string;
  /** Google's own docs report as folders; callers expand them separately. */
  isFolder: boolean;
};

/* The Picker ships no types; these are the members we actually touch. */
type PickerView = {
  setIncludeFolders: (v: boolean) => void;
  setSelectFolderEnabled: (v: boolean) => void;
  setOwnedByMe: (v: boolean) => void;
};

type PickerBuilderLike = {
  setOAuthToken: (t: string) => PickerBuilderLike;
  setDeveloperKey: (k: string) => PickerBuilderLike;
  setAppId: (id: string) => PickerBuilderLike;
  addView: (v: PickerView) => PickerBuilderLike;
  setCallback: (cb: (data: Record<string, unknown>) => void) => PickerBuilderLike;
  enableFeature: (f: string) => PickerBuilderLike;
  build: () => { setVisible: (v: boolean) => void };
};

type PickerGlobal = {
  picker: {
    PickerBuilder: new () => PickerBuilderLike;
    DocsView: new (viewId?: unknown) => PickerView;
    ViewId: Record<string, unknown>;
    Action: Record<string, string>;
    Feature: Record<string, string>;
    Response: Record<string, string>;
    Document: Record<string, string>;
  };
};

declare global {
  interface Window {
    gapi?: {
      load: (name: string, cb: () => void) => void;
    };
    google?: { picker?: PickerGlobal["picker"] };
  }
}

let scriptPromise: Promise<void> | null = null;

/** Loads apis.js once per page and resolves when the picker module is ready. */
function loadPicker(): Promise<void> {
  if (window.google?.picker) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${PICKER_SCRIPT_SRC}"]`
    );

    const onReady = () => {
      if (!window.gapi) {
        reject(new Error("Google API script loaded without gapi"));
        return;
      }
      window.gapi.load("picker", () => resolve());
    };

    if (existing) {
      if (window.gapi) onReady();
      else existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load the Google API script")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = PICKER_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () =>
      reject(new Error("Failed to load the Google API script"));
    document.body.appendChild(script);
  }).catch((error) => {
    // Don't cache a failure — a transient network error shouldn't permanently
    // break the picker for the rest of the session.
    scriptPromise = null;
    throw error;
  });

  return scriptPromise;
}

export type OpenPickerOptions = {
  /** A `drive.file`-scoped OAuth access token. */
  token: string;
  /** Browser API key restricted to the Picker API. */
  apiKey: string;
  /**
   * The Cloud project *number*, not the client id. Getting this wrong is the
   * usual cause of a 404 when the backend later fetches a picked file.
   */
  appId: string;
  multiselect?: boolean;
  /** Let the user pick folders as well as files. */
  allowFolders?: boolean;
};

/**
 * Opens the Picker and resolves with what the user chose — an empty array if
 * they cancelled.
 */
export function openDrivePicker({
  token,
  apiKey,
  appId,
  multiselect = true,
  allowFolders = false,
}: OpenPickerOptions): Promise<PickedFile[]> {
  return loadPicker().then(
    () =>
      new Promise<PickedFile[]>((resolve) => {
        const picker = window.google!.picker!;

        const view = new picker.DocsView(picker.ViewId.DOCS);
        view.setIncludeFolders(allowFolders);
        view.setSelectFolderEnabled(allowFolders);
        // Without this the Picker only offers files the user owns, which
        // silently hides anything shared with them — a common way to receive a
        // data room.
        view.setOwnedByMe(false);

        const builder = new picker.PickerBuilder()
          .setOAuthToken(token)
          .setDeveloperKey(apiKey)
          .setAppId(appId)
          .addView(view)
          .setCallback((data: Record<string, unknown>) => {
            const action = data[picker.Response.ACTION as string];
            if (action === picker.Action.PICKED) {
              const docs = (data[picker.Response.DOCUMENTS as string] ??
                []) as Record<string, string>[];
              resolve(
                docs.map((doc) => ({
                  id: doc[picker.Document.ID],
                  name: doc[picker.Document.NAME],
                  mimeType: doc[picker.Document.MIME_TYPE],
                  size: doc.sizeBytes ? Number(doc.sizeBytes) : undefined,
                  webViewLink: doc[picker.Document.URL],
                  isFolder:
                    doc[picker.Document.MIME_TYPE] ===
                    "application/vnd.google-apps.folder",
                }))
              );
            } else if (action === picker.Action.CANCEL) {
              resolve([]);
            }
          });

        if (multiselect) {
          builder.enableFeature(picker.Feature.MULTISELECT_ENABLED);
        }

        builder.build().setVisible(true);
      })
  );
}
